import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Chip, Tooltip, IconButton, Divider, useTheme,
} from '@mui/material';
import DashboardIcon    from '@mui/icons-material/Dashboard';
import ApartmentIcon    from '@mui/icons-material/Apartment';
import PeopleIcon       from '@mui/icons-material/People';
import LogoutIcon       from '@mui/icons-material/Logout';
import HistoryIcon      from '@mui/icons-material/History';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import SyncIcon         from '@mui/icons-material/Sync';
import CloudUploadIcon  from '@mui/icons-material/CloudUpload';
import SettingsIcon     from '@mui/icons-material/Settings';
import BarChartIcon     from '@mui/icons-material/BarChart';
import LightModeIcon    from '@mui/icons-material/LightMode';
import DarkModeIcon     from '@mui/icons-material/DarkMode';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth }      from '../utils/AuthContext';
import { useThemeMode } from '../utils/ThemeContext';

const DRAWER_EXPANDED = 220;
const DRAWER_COLLAPSED = 60;

const NAV = [
  { label: 'Dashboard', path: '/',         icon: <DashboardIcon />,    role: 'readonly' },
  { label: 'Tenants',   path: '/tenants',  icon: <ApartmentIcon />,    role: 'readonly' },
  { label: 'Sync',      path: '/sync',     icon: <SyncIcon />,         role: 'operator' },
  { label: 'Provision', path: '/provision', icon: <CloudUploadIcon />, role: 'admin' },
  { label: 'Scan',      path: '/scan',     icon: <TroubleshootIcon />, role: 'readonly' },
  { label: 'Users',     path: '/users',    icon: <PeopleIcon />,       role: 'admin' },
  { label: 'Analytics', path: '/analytics', icon: <BarChartIcon />,    role: 'readonly' },
  { label: 'Audit Log', path: '/audit',    icon: <HistoryIcon />,      role: 'readonly' },
  { label: 'Settings',  path: '/settings', icon: <SettingsIcon />,     role: 'admin' },
];

const ROLE_COLORS = {
  super_admin: '#7c3aed',
  admin:       '#3b82f6',
  operator:    '#16a34a',
  readonly:    '#6b778c',
};

export default function Layout({ children }) {
  const { user, logout, hasRole } = useAuth();
  const { mode, toggleMode }      = useThemeMode();
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const dark      = mode === 'dark';

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === '1'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem('sidebar_collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const drawerW       = collapsed ? DRAWER_COLLAPSED : DRAWER_EXPANDED;
  const sidebarBg     = dark ? '#0d1117' : '#0f1f35';
  const activeBg      = dark ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.22)';
  const hoverBg       = dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.07)';
  const borderColor   = 'rgba(255,255,255,0.07)';
  const textMuted     = 'rgba(255,255,255,0.5)';
  const textActive    = '#fff';
  const iconActive    = '#93c5fd';
  const iconInactive  = 'rgba(255,255,255,0.45)';

  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: sidebarBg, overflow: 'hidden' }}>
      {/* Logo / toggle row */}
      <Box sx={{
        px: collapsed ? 0 : 2,
        pt: 2.5, pb: 2,
        borderBottom: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: 64,
      }}>
        {!collapsed && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', letterSpacing: 0.3, lineHeight: 1.2, fontSize: '1rem' }}>
              HOA Control
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.58rem' }}>
              Admin Panel
            </Typography>
          </Box>
        )}
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            sx={{
              color: 'rgba(255,255,255,0.4)',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              width: 28, height: 28,
              flexShrink: 0,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
            }}
          >
            {collapsed ? <ChevronRightIcon sx={{ fontSize: 16 }} /> : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Nav */}
      <List sx={{ px: collapsed ? 0.75 : 1.25, py: 1.5, flex: 1 }}>
        {NAV.filter(n => hasRole(n.role)).map(item => {
          const active = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          const btn = (
            <ListItemButton key={item.path} component={Link} to={item.path}
              sx={{
                borderRadius: '10px', mb: 0.5,
                py: collapsed ? 1 : 0.85,
                px: collapsed ? 0 : 1.25,
                justifyContent: collapsed ? 'center' : 'flex-start',
                backgroundColor: active ? activeBg : 'transparent',
                borderLeft: collapsed ? 'none' : (active ? '2px solid #3b82f6' : '2px solid transparent'),
                transition: 'background-color 0.15s ease',
                minWidth: 0,
                '&:hover': { backgroundColor: active ? activeBg : hoverBg },
              }}
            >
              <ListItemIcon sx={{
                minWidth: collapsed ? 0 : 34,
                color: active ? iconActive : iconInactive,
                justifyContent: 'center',
              }}>
                {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.83rem',
                    fontWeight: active ? 600 : 400,
                    color: active ? textActive : textMuted,
                    letterSpacing: 0.1,
                    noWrap: true,
                  }}
                />
              )}
            </ListItemButton>
          );
          return collapsed
            ? <Tooltip key={item.path} title={item.label} placement="right">{btn}</Tooltip>
            : btn;
        })}
      </List>

      {/* User footer */}
      <Box sx={{ px: collapsed ? 0.75 : 1.25, py: 1.5, borderTop: `1px solid ${borderColor}` }}>
        {/* Theme toggle */}
        <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', mb: 1.25 }}>
          <Tooltip title={dark ? 'Switch to light mode' : 'Switch to dark mode'} placement="right">
            <IconButton
              size="small"
              onClick={toggleMode}
              sx={{
                color: 'rgba(255,255,255,0.45)',
                backgroundColor: 'rgba(255,255,255,0.06)',
                borderRadius: '8px',
                px: 1,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
              }}
            >
              {dark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {collapsed ? (
          <Tooltip title={`${user?.username} (${user?.role?.replace('_', ' ')})`} placement="right">
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
              <Avatar sx={{
                width: 30, height: 30, fontSize: '0.75rem', cursor: 'default',
                backgroundColor: ROLE_COLORS[user?.role] || '#3b82f6',
                fontWeight: 700,
              }}>
                {user?.username?.[0]?.toUpperCase()}
              </Avatar>
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{
              width: 30, height: 30, fontSize: '0.78rem', flexShrink: 0,
              backgroundColor: ROLE_COLORS[user?.role] || '#3b82f6',
              fontWeight: 700,
            }}>
              {user?.username?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                {user?.username}
              </Typography>
              <Chip
                label={user?.role?.replace('_', ' ')}
                size="small"
                sx={{ height: 14, fontSize: '0.58rem', fontWeight: 700, backgroundColor: ROLE_COLORS[user?.role], color: '#fff', mt: 0.2 }}
              />
            </Box>
            <Tooltip title="Sign out">
              <IconButton
                size="small"
                onClick={() => { logout(); navigate('/login'); }}
                sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#f87171' }, flexShrink: 0 }}
              >
                <LogoutIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {collapsed && (
          <Tooltip title="Sign out" placement="right">
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.75 }}>
              <IconButton
                size="small"
                onClick={() => { logout(); navigate('/login'); }}
                sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#f87171' } }}
              >
                <LogoutIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerW,
          flexShrink: 0,
          transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
          '& .MuiDrawer-paper': {
            width: drawerW,
            border: 'none',
            overflowX: 'hidden',
            transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: dark ? '4px 0 24px rgba(0,0,0,0.5)' : '4px 0 24px rgba(0,0,0,0.1)',
          },
        }}
      >
        {sidebar}
      </Drawer>

      <Box sx={{ flex: 1, p: { xs: 2, md: 2.5 }, overflow: 'auto', minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
}
